import '../App.css';
import React, { useEffect } from 'react';
import { auth } from '../firebase.js';
import { useState } from 'react';
import { signInAnonymously } from "firebase/auth";
import { logDemographics, logNewUser, logPostHighlightSurvey } from '../logging.js';
import { TextField, Image, Flex, Button, Form, Provider, defaultTheme, Text, Heading, Radio, RadioGroup, ContextualHelp, Content, CheckboxGroup, Checkbox, TextArea, Slider } from '@adobe/react-spectrum';

import nudgingTooltip from '../misc-interfaces/images/nudging-tooltip.png';
import nudgingTimeWait from '../misc-interfaces/images/nudging-time-wait.png';
import nudgingRedact from '../misc-interfaces/images/nudging-redact.png';
import nudgingCircle from '../misc-interfaces/images/nudging-circle.png';

function NudgingPostHighlightForm({ currentIndex, participantId, workerId, condition, experiment, maxWordCount, specificExample, callbacks }) {
    // Results
    const [mentalDemand, setMentalDemand] = useState(-1);
    const [physicalDemand, setPhysicalDemand] = useState(-1);
    const [temporalDemand, setTemporalDemand] = useState(-1);
    const [performance, setPerformance] = useState(-1);
    const [effort, setEffort] = useState(-1);
    const [frustration, setFrustration] = useState(-1);

    const [techniqueThoughts, setTechniqueThoughts] = useState('');
    const [awareness, setAwareness] = useState(-1);
    const [technicalIssues, setTechnicalIssues] = useState('');

    const [additionalComments, setAdditionalComments] = useState('');

    // Validation
    const [formValidated, setFormValidated] = useState(false);

    useEffect(() => {
        validateForm();
    }, [mentalDemand, physicalDemand, temporalDemand, performance, effort, frustration, techniqueThoughts, awareness, technicalIssues, additionalComments]);

    function validateForm() {
        if (mentalDemand > -1 && physicalDemand > -1 && temporalDemand > -1 && performance > -1 && effort > -1 && frustration > -1 && techniqueThoughts !== '' && awareness > -1) {
            setFormValidated(true);
        }

        else {
            setFormValidated(false);
        }
    }

    function submitForm(e) {
        e.preventDefault();
        let results = {
            timestamp: Date.now(),
            workerId: workerId,
            participantId: participantId,
            condition: condition,
            experiment: experiment,

            mentalDemand: mentalDemand,
            physicalDemand: physicalDemand,
            temporalDemand: temporalDemand,
            performance: performance,
            effort: effort,
            frustration: frustration,
            awareness: awareness,

            techniqueThoughts: techniqueThoughts,

            technicalIssues: technicalIssues,
            additionalComments: additionalComments,
        }

        logPostHighlightSurvey(results, callbacks);
    }

    return (
        <>
            <Provider theme={defaultTheme} colorScheme='light'>
                <Flex direction="column" justifyContent={'center'} alignItems={"center"}>
                    <div id="textToRead" className='documentReader'>
                        <Heading level={1} width="100%" marginBottom="size-100" marginTop="size-0">Post-Highlighting Questions</Heading>
                        <Text>Please answer the following questions about your experience highlighting the document using the provided document reader. <b>All questions are mandatory unless indicated otherwise.</b></Text>

                        {/* {currentIndex === 0 ? */}
                        <><br /><br /><Text><b>You WILL be able to edit your responses later.</b></Text></>
                        {/* : null} */}

                        <Form onSubmit={e => submitForm(e)}>
                            <Heading level={2} width="100%" marginBottom="size-100" marginTop="size-200">Task Workload</Heading>
                            {/* Mental Demand */}
                            {/* <RadioGroup marginBottom="size-100" onChange={setMentalDemand} orientation='horizontal' label={<Heading level={3} width="100%" marginBottom="size-100" marginTop="size-0">How mentally demanding was it to highlight the document?</Heading>}>
                                <Text marginEnd={"size-500"} marginTop={"size-50"}>Very low</Text>
                                {[...Array(7)].map((e, i) => <Radio value={(i + 1)}>{(i + 1)}</Radio>)}
                                <Text marginStart={"size-500"} marginTop={"size-50"}>Very high</Text>
                            </RadioGroup> */}

                            <Heading level={4} width="100%" marginTop="size-0" marginBottom="size-50">How mentally demanding was it to highlight the document? {mentalDemand > -1 ? <span style={{ marginLeft: 20, fontFamily: 'monospace' }}>{mentalDemand}/100</span> : <span style={{ color: 'red', marginLeft: 20, fontFamily: 'monospace' }}>/100</span>} </Heading>
                            <Flex direction="row" alignItems="center" justifyContent="space-between" marginBottom="size-50">
                                <Text marginEnd={"size-500"} marginTop={"size-50"}>Very low</Text>
                                <Slider isFilled width={"70%"} minValue={0} maxValue={100} step={1} valueLabelDisplay="on" onChange={setMentalDemand} />
                                <Text marginStart={"size-500"} marginTop={"size-50"}>Very high</Text>
                            </Flex>

                            {/* Physical Demand */}
                            <Heading level={4} width="100%" marginTop="size-0" marginBottom="size-50">How physically demanding was it to highlight the document? {physicalDemand > -1 ? <span style={{ marginLeft: 20, fontFamily: 'monospace' }}>{physicalDemand}/100</span> : <span style={{ color: 'red', marginLeft: 20, fontFamily: 'monospace' }}>/100</span>} </Heading>
                            <Flex direction="row" alignItems="center" justifyContent="space-between" marginBottom="size-50">
                                <Text marginEnd={"size-500"} marginTop={"size-50"}>Very low</Text>
                                <Slider isFilled width={"70%"} minValue={0} maxValue={100} step={1} valueLabelDisplay="on" onChange={setPhysicalDemand} />
                                <Text marginStart={"size-500"} marginTop={"size-50"}>Very high</Text>
                            </Flex>

                            {/* Temporal Demand */}
                            <Heading level={4} width="100%" marginTop="size-0" marginBottom="size-50">How hurried or rushed was it to highlight the document? {temporalDemand > -1 ? <span style={{ marginLeft: 20, fontFamily: 'monospace' }}>{temporalDemand}/100</span> : <span style={{ color: 'red', marginLeft: 20, fontFamily: 'monospace' }}>/100</span>} </Heading>
                            <Flex direction="row" alignItems="center" justifyContent="space-between" marginBottom="size-50">
                                <Text marginEnd={"size-500"} marginTop={"size-50"}>Very low</Text>
                                <Slider isFilled width={"70%"} minValue={0} maxValue={100} step={1} valueLabelDisplay="on" onChange={setTemporalDemand} />
                                <Text marginStart={"size-500"} marginTop={"size-50"}>Very high</Text>
                            </Flex>

                            {/* Performance */}
                            <Heading level={4} width="100%" marginTop="size-0" marginBottom="size-50">How successful were you at highlighting the document? {performance > -1 ? <span style={{ marginLeft: 20, fontFamily: 'monospace' }}>{performance}/100</span> : <span style={{ color: 'red', marginLeft: 20, fontFamily: 'monospace' }}>/100</span>} </Heading>
                            <Flex direction="row" alignItems="center" justifyContent="space-between" marginBottom="size-50">
                                <Text marginEnd={"size-500"} marginTop={"size-50"}>Failure</Text>
                                <Slider isFilled width={"70%"} minValue={0} maxValue={100} step={1} valueLabelDisplay="on" onChange={setPerformance} />
                                <Text marginStart={"size-500"} marginTop={"size-50"}>Perfect</Text>
                            </Flex>

                            {/* Effort */}
                            <Heading level={4} width="100%" marginTop="size-0" marginBottom="size-50">How hard did you have to work to highlight the document? {effort > -1 ? <span style={{ marginLeft: 20, fontFamily: 'monospace' }}>{effort}/100</span> : <span style={{ color: 'red', marginLeft: 20, fontFamily: 'monospace' }}>/100</span>} </Heading>
                            <Flex direction="row" alignItems="center" justifyContent="space-between" marginBottom="size-50">
                                <Text marginEnd={"size-500"} marginTop={"size-50"}>Very low</Text>
                                <Slider isFilled width={"70%"} minValue={0} maxValue={100} step={1} valueLabelDisplay="on" onChange={setEffort} />
                                <Text marginStart={"size-500"} marginTop={"size-50"}>Very high</Text>
                            </Flex>


                            {/* Frustration */}
                            <Heading level={4} width="100%" marginTop="size-0" marginBottom="size-50">How frustrated (i.e., irritated, stressed, and annoyed) were you? {frustration > -1 ? <span style={{ marginLeft: 20, fontFamily: 'monospace' }}>{frustration}/100</span> : <span style={{ color: 'red', marginLeft: 20, fontFamily: 'monospace' }}>/100</span>} </Heading>
                            <Flex direction="row" alignItems="center" justifyContent="space-between" marginBottom="size-50">
                                <Text marginEnd={"size-500"} marginTop={"size-50"}>Very low</Text>
                                <Slider isFilled width={"70%"} minValue={0} maxValue={100} step={1} valueLabelDisplay="on" onChange={setFrustration} />
                                <Text marginStart={"size-500"} marginTop={"size-50"}>Very high</Text>
                            </Flex>


                            {/* Amount of Text Awareness */}
                            <Heading level={4} width="100%" marginTop="size-0" marginBottom="size-50">I felt aware of what and how I was highlighting. {awareness > -1 ? <span style={{ marginLeft: 20, fontFamily: 'monospace' }}>{awareness}/100</span> : <span style={{ color: 'red', marginLeft: 20, fontFamily: 'monospace' }}>/100</span>} </Heading>
                            <Flex direction="row" alignItems="center" justifyContent="space-between" marginBottom="size-50">
                                <Text marginEnd={"size-500"} marginTop={"size-50"}>Strongly disagree</Text>
                                <Slider isFilled width={"70%"} minValue={0} maxValue={100} step={1} valueLabelDisplay="on" onChange={setAwareness} />
                                <Text marginStart={"size-500"} marginTop={"size-50"}>Strongly agree</Text>
                            </Flex>


                            <Heading level={2} width="100%" marginBottom="size-100" marginTop="size-200">Technique Feedback</Heading>

                            {/* Experience */}
                            {
                                condition === "time" ?
                                    <>
                                        <Text>When creating new highlights, <b>you had to wait for a gauge to fill up</b>. Answer the following questions about this specific feature.</Text>
                                        <Image marginBottom={"size-200"} alignSelf={"center"} src={nudgingTimeWait} width={'25%'} alignItems={"center"} alt=""></Image>
                                    </>
                                    : condition === "trailing" ?
                                        <>
                                        <Text>When creating new highlights, <b>you had to keep your cursor inside a circle</b>. Answer the following questions about this specific feature.</Text>
                                            <Image marginBottom={"size-200"} alignSelf={"center"} src={nudgingCircle} width={'25%'} alignItems={"center"} alt=""></Image>
                                        </>
                                        : condition === "redact" ?
                                            <>
                                                <Text>When creating new highlights, <b>the document text became obscured (the text background got darker)</b>. Answer the following questions about this specific feature.</Text>
                                                <Image marginBottom={"size-200"} alignSelf={"center"} src={nudgingRedact} width={'25%'} alignItems={"center"} alt=""></Image>

                                            </>
                                            : condition === "tooltip" ?
                                                <>
                                                    <Text>When creating new highlights, <b>a short message appeared above your cursor</b>. Answer the following questions about this specific feature.</Text>
                                                    <Image marginBottom={"size-200"} alignSelf={"center"} src={nudgingTooltip} width={'25%'} alignItems={"center"} alt=""></Image>
                                                </>
                                                : null
                            }

                            {/* Technique Thoughts */}
                            <TextArea marginBottom="size-100" onChange={setTechniqueThoughts} label={<Heading level={3} width="100%" marginBottom="size-100" marginTop="size-0">Focusing on 2 or 3 points, what did you think of this feature?</Heading>} />

                            <Heading level={2} width="100%" marginBottom="size-100" marginTop="size-200">Other</Heading>

                            {/* Technical Issues */}
                            <TextArea marginBottom="size-100" onChange={setTechnicalIssues} label={<Heading level={3} width="100%" marginBottom="size-100" marginTop="size-0">Did you experience any technical issues when using the document reader? If so, please describe them below. This will NOT impact task approval. (optional)</Heading>} />

                            {/* Additional Comments */}
                            <TextArea marginBottom="size-100" onChange={setAdditionalComments} label={<Heading level={3} width="100%" marginBottom="size-100" marginTop="size-0">Do you have any additional comments about your experience using the document viewer? (optional)</Heading>} />

                            <Button variant='primary' type="submit" style='fill' marginTop="size-600" isDisabled={!formValidated}>Submit</Button>
                        </Form>
                    </div>
                </Flex>
            </Provider>
        </>
    );
}

export default NudgingPostHighlightForm;